
import { useParams } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FavoriteStar } from "@/components/ui/favorite-star"
import { categories, getAvailableGames } from "@/lib/games-data"
import { useTranslation } from "@/hooks/use-translation"
import { SEOMeta } from "@/components/seo/seo-meta"
import { ResponsiveAd, InContentAd } from "@/components/adsense"

export function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>()
  const { t, navigate, language } = useTranslation()

  const category = categories.find((c) => c.id === categoryId)
  const availableGames = getAvailableGames(language)
  const categoryGames = availableGames.filter((game) => game.category === categoryId)

  if (!category) {
    return (
      <>
        <SEOMeta
          title={t("errors.categoryNotFound")}
          description="The requested category does not exist"
        />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t("errors.categoryNotFound")}
            </h1>
            <Button onClick={() => navigate("/")}>
              {t("navigation.backToHome")}
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEOMeta
        title={`${t(`categories.${category.id}.name`)} - ${t('app.title')}`}
        description={t(`categories.${category.id}.description`)}
        keywords={`${t(`categories.${category.id}.name`)}, games, ${category.id}, ${t("navigation.dailyGames").toLowerCase()}`}
      />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 mb-4">
            <category.icon className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
                {t(`categories.${category.id}.name`)}
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground">
                {t(`categories.${category.id}.description`)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {t("home.totalGames", { count: categoryGames.length })}
            </Badge>
            <Badge variant="outline" className="text-xs">{t("category.dailyGames")}</Badge>
          </div>
        </div>

        {/* Advertisement */}
        <div className="mb-6 sm:mb-8">
          <ResponsiveAd className="mx-auto max-w-4xl" />
        </div>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">{t("category.availableGames")}</h2>
          <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {categoryGames.map((game) => (
              <Card
                key={game.id}
                className="group hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/game/${game.id}`)}
              >
                <CardHeader className="pb-3 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <game.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="text-base sm:text-lg line-clamp-1">
                            {t(`games.${game.id}.name`)}
                          </CardTitle>
                          <FavoriteStar toolId={game.id} size="sm" className="flex-shrink-0" />
                        </div>
                        <CardDescription className="text-sm line-clamp-2">
                          {t(`games.${game.id}.description`)}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <div className="mt-3 flex flex-wrap gap-1">
                    {game.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {t(`tags.${tag}`)}
                      </Badge>
                    ))}
                    {game.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{game.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {categoryGames.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {t("errors.noGamesInCategory")}
            </p>
          </div>
        )}

        {/* Bottom Advertisement */}
        <div className="mt-8">
          <InContentAd className="mx-auto max-w-4xl" />
        </div>
      </div>
    </>
  )
} 